import { ProductDetailsSections, ProductPage } from '@api/types';
import {
  ProductAccessories,
  ProductDealerInfo,
  ProductDetailsContext,
  ProductDetailsContextType,
  ProductDetailsTabs,
  ProductDocuments,
  ProductImageGallery,
  ProductLogo,
  ProductLongDescription,
  ProductOrderForm,
  ProductPackageInfo,
  ProductPages,
  ProductPriceInfo,
  ProductRequestOrder,
  ProductSpecifications,
  ProductStockInfo,
  ProductTitle,
  ProductUsp,
  ProductVariantSelector,
  SimilarProducts,
  UpsaleProducts,
} from '@components';
import { useAppDispatch, useAuth } from '@hooks';
import { Box, Container, Grid, Stack, styled } from '@mui/material';
import { setDetailsSection } from '@store/product/product.slice';
import { breakpoints } from '@themes/theme.helpers';
import {
  getDetailsWithParentProductData,
  productHasAccessories,
  productHasDescription,
  productHasDocuments,
  productHasRelatedMaterials,
  productHasSimilarProducts,
  productHasSpecifications,
  productHasUpsaleProducts,
} from '@utils/products.utils';
import { useMemo } from 'react';

// STYLES
//--------

const ProductDetailsWrapper = styled(Box)`
  ${breakpoints.up('md')} {
    padding: 0 0 64px 0;
  }
`;
// TYPES
//-------

export type ProductDetailsProps = {
  product?: ProductPage;
  requestOrderEnabled?: boolean;
  requestInfoEnabled?: boolean;
};

// COMPONENTS
//------------
export const ProductDetails = ({
  product = {},
  requestOrderEnabled,
  requestInfoEnabled,
}: ProductDetailsProps) => {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAuth();
  // Context and different sections
  const contextValue: ProductDetailsContextType = useMemo(() => {
    const data = getDetailsWithParentProductData(product);
    return {
      product: data,
      sections: {
        description: productHasDescription(data),
        specifications: productHasSpecifications(data),
        documents: productHasDocuments(data),
        accessories: productHasAccessories(data),
        similar: productHasSimilarProducts(data),
        related: productHasRelatedMaterials(data),
        upsale: productHasUpsaleProducts(data),
      },
      onSectionEnter: (sectionId) =>
        dispatch(setDetailsSection(sectionId as ProductDetailsSections)),
    };
  }, [product]);

  return (
    <ProductDetailsContext.Provider value={contextValue}>
      <ProductDetailsWrapper className='product-details'>
        <Container>
          <ProductLogo />
          <Grid container spacing={4}>
            <Grid item xs={12} md={7}>
              <ProductImageGallery />
            </Grid>
            <Grid item xs={12} md={5}>
              <Stack gap={2}>
                <ProductTitle />
                <ProductUsp />
                <ProductPackageInfo />
                {isAuthenticated && <ProductStockInfo />}
                <ProductPriceInfo requestInfoEnabled={requestInfoEnabled} />
                {requestOrderEnabled && <ProductRequestOrder />}
                <Box
                  sx={{
                    mb: {
                      xs: 4,
                      md: 0,
                    },
                  }}
                >
                  <ProductDealerInfo />
                </Box>
                <ProductOrderForm />
                <ProductVariantSelector />
              </Stack>
            </Grid>
          </Grid>
          <ProductDetailsTabs />
          <Stack spacing={2}>
            <ProductLongDescription />
            <ProductSpecifications />
            <ProductDocuments />
            <ProductAccessories />
            <SimilarProducts />
            <UpsaleProducts />
            <ProductPages />
          </Stack>
        </Container>
      </ProductDetailsWrapper>
    </ProductDetailsContext.Provider>
  );
};
